import { inject, Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  IResponsePaginate,
  Company,
  BaseQueryParams,
  IResponse,
  WithMetadata,
  Address,
  BankDetails,
  CompanyContact,
  AddressDto,
  BankDetailsDto,
  CompanyContactDto,
  CompanyContactExtras,
  CompanyDto,
  CompanyDetails,
} from '@/types';
import { Observable } from 'rxjs';

interface CompanyQuery extends BaseQueryParams {
  nom?: string;
  type?: string;
  nni?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CompanyService {
  BASEURL = `${environment.api.paramsUrl}/societe`;
  webSiteAuthUrl = `${environment.api.websiteAuthUrl}/societe`;
  httpClient = inject(HttpClient);

  getPendingRequests(params?: CompanyQuery): Observable<IResponsePaginate<Company>> {
    return this.httpClient.get<IResponsePaginate<Company>>(`${this.BASEURL}/status/en-cours`, { params: params as HttpParams });
  }

  validateCompany(id: number, isValid: boolean): Observable<any> {
    return this.httpClient.put(`${this.webSiteAuthUrl}/validate/${id}`, { approved: isValid });
  }

  getApprovedCompanies(params?: CompanyQuery): Observable<IResponsePaginate<Company>> {
    return this.httpClient.get<IResponsePaginate<Company>>(`${this.BASEURL}/status/validee`, { params: params as HttpParams });
  }

  getNonBlacklistedCompanies(params?: CompanyQuery): Observable<CompanyDetails[]> {
    return this.httpClient.get<CompanyDetails[]>(`${this.BASEURL}/all-valid-not-black-listed`, { params: params as HttpParams });
  }

  getOneCompany(id: number): Observable<WithMetadata<CompanyDetails>> {
    return this.httpClient.get<WithMetadata<CompanyDetails>>(`${this.BASEURL}/${id}`);
  }

  addCompany(company: CompanyDto): Observable<WithMetadata<Company>> {
    return this.httpClient.post<WithMetadata<Company>>(`${this.BASEURL}`, company);
  }

  updateCompany(id: number, company: CompanyDto): Observable<IResponse<Company>> {
    return this.httpClient.put<IResponse<Company>>(`${this.BASEURL}/${id}`, company);
  }

  deleteCompany(id: number): Observable<IResponse<Company>> {
    return this.httpClient.delete<IResponse<Company>>(`${this.BASEURL}/${id}`);
  }

  // related entities

  //fetch
  getCompanyContacts(id: number): Observable<IResponsePaginate<CompanyContact>> {
    return this.httpClient.get<IResponsePaginate<CompanyContact>>(`${this.BASEURL}/${id}/contact`);
  }

  getCompanyAddresses(id: number): Observable<IResponsePaginate<Address>> {
    return this.httpClient.get<IResponsePaginate<Address>>(`${this.BASEURL}/${id}/societe_address`);
  }

  getCompanyBankDetails(id: number): Observable<IResponsePaginate<BankDetails>> {
    return this.httpClient.get<IResponsePaginate<BankDetails>>(`${this.BASEURL}/${id}/compte_bancaire`);
  }

  //add
  addCompanyContact(id: number, contact: CompanyContactDto): Observable<IResponse<Company>> {
    return this.httpClient.post<IResponse<Company>>(`${this.BASEURL}/${id}/contact`, contact);
  }

  addCompanyAddress(id: number, address: AddressDto): Observable<IResponse<Company>> {
    return this.httpClient.post<IResponse<Company>>(`${this.BASEURL}/${id}/societe_address`, address);
  }

  addCompanyBankDetails(id: number, bankDetails: BankDetailsDto): Observable<IResponse<Company>> {
    return this.httpClient.post<IResponse<Company>>(`${this.BASEURL}/${id}/compte_bancaire`, bankDetails);
  }

  //update
  updateCompanyContact(id: number, contactId: number, contactExtras: CompanyContactExtras): Observable<IResponse<Company>> {
    return this.httpClient.put<IResponse<Company>>(`${this.BASEURL}/${id}/contact/${contactId}`, contactExtras);
  }

  downloadCompanyDocs(alfrescoId: string): Observable<{ file: string, erreur: boolean, msg: string, documentId: string }> {
    return this.httpClient.post<{ file: string, erreur: boolean, msg: string, documentId: string }>(`${this.BASEURL}/download/societe-docs`, { id: alfrescoId });
  }
}
